import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormFieldNewComponent, PhoneMaskFixDirective } from '@sdvor/shared/ui';
import { SdvorButtonComponent } from '@sdvor/shared/ui/v2';
import { ButtonSizes, ButtonThemes } from '@shared/v2';
import { UserProfileFacade } from '@spartacus/user';
import { User } from '@user/const';
import { filter } from 'rxjs';
import { UserEmailFormComponent } from '../user-email-form';

@Component({
  selector: 'sd-custom-update-profile',
  templateUrl: './custom-update-profile.component.html',
  styleUrls: ['./custom-update-profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UserEmailFormComponent,
    ReactiveFormsModule,
    FormFieldNewComponent,
    SdvorButtonComponent,
    PhoneMaskFixDirective,
  ],
})
export class CustomUpdateProfileComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly userProfileFacade = inject(UserProfileFacade);

  protected isFormEditing = signal(false);

  protected buttonThemes = ButtonThemes;
  protected buttonSizes = ButtonSizes;

  protected form = this.formBuilder.nonNullable.group({
    phone: [{ value: '', disabled: true }],
    firstName: ['', [Validators.required, Validators.minLength(2)]],
    lastName: ['', [Validators.minLength(2)]],
    dateOfBirth: ['', { validators: [Validators.minLength(8)] }],
    // если не передавать актуальное значение этого поля, то бекенд затирает его
    marketingEmailsConsent: [false],
  });

  private initialValue = this.form.getRawValue();

  ngOnInit(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isFormEditing.set(true);
    });

    this.userProfileFacade
      .get()
      .pipe(
        filter((user): user is User => Boolean(user)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((user) => {
        this.initialValue = {
          phone: user.phone ? user.phone.slice(1) : '',
          firstName: user.firstName ?? '',
          lastName: user.lastName ?? '',
          dateOfBirth: user.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : '',
          marketingEmailsConsent: user.marketingEmailsConsent ?? false,
        };

        this.form.setValue(this.initialValue, { emitEvent: false });
      });
  }

  protected onSubmit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();

      return;
    }

    const formattedValue = {
      ...this.form.value,
      dateOfBirth: this.form.value.dateOfBirth
        ? new Date(this.form.value.dateOfBirth as string).toISOString().replace('.000Z', '+0000')
        : '',
    };

    this.userProfileFacade.update(formattedValue).subscribe();
    this.isFormEditing.set(false);
  }

  protected onReset(): void {
    this.form.setValue(this.initialValue, { emitEvent: false });
    this.isFormEditing.set(false);
  }
}
